import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from '../../../types/Platforms';
import { Plus, X } from 'lucide-react';
import { SupplierLogo } from '../../ui/SupplierLogo';

interface Props {
  platform: Platform;
  orderClientNames: string[];
  orderEmails: string[];
  onChangeClientNames: (names: string[]) => void;
  onChangeEmails: (emails: string[]) => void;
  onNext: () => void;
}

const ConfigureSupplierStep: React.FC<Props> = ({
  platform,
  orderClientNames,
  orderEmails,
  onChangeClientNames,
  onChangeEmails,
  onNext,
}) => {
  const { t } = useTranslation();
  const [emailInput, setEmailInput] = useState('');
  const [clientNameInput, setClientNameInput] = useState('');

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (trimmed && !orderEmails.includes(trimmed)) {
      onChangeEmails([...orderEmails, trimmed]);
      setEmailInput('');
    }
  };

  const removeEmail = (email: string) => {
    onChangeEmails(orderEmails.filter((e) => e !== email));
  };

  const addClientName = () => {
    const trimmed = clientNameInput.trim();
    if (trimmed && !orderClientNames.includes(trimmed)) {
      onChangeClientNames([...orderClientNames, trimmed]);
      setClientNameInput('');
    }
  };

  const removeClientName = (name: string) => {
    onChangeClientNames(orderClientNames.filter((n) => n !== name));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  const handleClientNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addClientName();
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text dark:text-text-dark">{t('wizard.configureSupplier.title')}</h2>
        <p className="text-muted dark:text-muted-dark mt-2">{t('wizard.configureSupplier.subtitle')}</p>
      </div>

      <div className="flex items-center justify-center gap-3 p-4 rounded-xl bg-zinc-50 dark:bg-zinc-800/50">
        <SupplierLogo platform={platform} alt={platform} size="large" />
        <span className="font-semibold text-text dark:text-text-dark capitalize">{platform.replace(/_/g, ' ')}</span>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text dark:text-text-dark mb-1">
            {t('wizard.configureSupplier.clientNameLabel')}
          </label>
          <p className="text-xs text-muted dark:text-muted-dark mb-2">
            {t('wizard.configureSupplier.clientNameHint')}
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={clientNameInput}
              onChange={(e) => setClientNameInput(e.target.value)}
              onKeyDown={handleClientNameKeyDown}
              placeholder={t('wizard.configureSupplier.clientNamePlaceholder')}
              className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-background dark:bg-background-dark text-text dark:text-text-dark"
            />
            <button
              onClick={addClientName}
              disabled={!clientNameInput.trim()}
              className="px-3 py-2 rounded-lg bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {orderClientNames.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {orderClientNames.map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-zinc-100 dark:bg-zinc-700 text-sm text-text dark:text-text-dark"
                >
                  {name}
                  <button onClick={() => removeClientName(name)} className="hover:text-red-500 dark:hover:text-red-400 transition">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-text dark:text-text-dark mb-1">
            {t('wizard.configureSupplier.emailsLabel')}
          </label>
          <p className="text-xs text-muted dark:text-muted-dark mb-2">
            {t('wizard.configureSupplier.emailsHint')}
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('wizard.configureSupplier.emailPlaceholder')}
              className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-background dark:bg-background-dark text-text dark:text-text-dark"
            />
            <button
              onClick={addEmail}
              disabled={!emailInput.trim()}
              className="px-3 py-2 rounded-lg bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {orderEmails.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {orderEmails.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-zinc-100 dark:bg-zinc-700 text-sm text-text dark:text-text-dark"
                >
                  {email}
                  <button onClick={() => removeEmail(email)} className="hover:text-red-500 dark:hover:text-red-400 transition">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => {
            const trimmedEmail = emailInput.trim();
            if (trimmedEmail && !orderEmails.includes(trimmedEmail)) {
              onChangeEmails([...orderEmails, trimmedEmail]);
            }
            const trimmedName = clientNameInput.trim();
            if (trimmedName && !orderClientNames.includes(trimmedName)) {
              onChangeClientNames([...orderClientNames, trimmedName]);
            }
            onNext();
          }}
          className="px-6 py-3 rounded-xl bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition font-medium"
        >
          {t('wizard.next')}
        </button>
      </div>
    </div>
  );
};

export default ConfigureSupplierStep;
